import { useLocation } from 'react-router-dom';
import { useProfile } from '@/hooks/useProfile';
import { Button } from '@/components/ui/button';
import { Target, TrendingUp, Sparkles, Lightbulb, Map, CheckCircle2, Compass } from 'lucide-react';

interface QuickStart {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  action: string;
}

const QUICK_STARTS: Record<string, QuickStart[]> = {
  home: [
    { label: 'What should I focus on?', icon: Target, action: 'skill_gap' },
    { label: 'Review my week', icon: TrendingUp, action: 'progress_coaching' },
    { label: 'Suggest new goals', icon: Sparkles, action: 'goal_suggestions' },
  ],
  goals: [
    { label: 'Analyze skill gaps', icon: Target, action: 'skill_gap' },
    { label: 'Suggest goals', icon: Sparkles, action: 'goal_suggestions' },
    { label: 'Review progress', icon: TrendingUp, action: 'progress_coaching' },
  ],
  plan: [
    { label: 'Explain this milestone', icon: Map, action: 'progress_coaching' },
    { label: 'Suggest next steps', icon: Lightbulb, action: 'goal_suggestions' },
    { label: 'Check skill gaps', icon: Target, action: 'skill_gap' },
  ],
  habits: [
    { label: 'Why am I struggling?', icon: Lightbulb, action: 'progress_coaching' },
    { label: 'Optimize my routine', icon: CheckCircle2, action: 'goal_suggestions' },
    { label: 'Set new goals', icon: Target, action: 'goal_suggestions' },
  ],
};

const CAREER_PATH_QUICK_START: QuickStart = {
  label: 'Explore career paths',
  icon: Compass,
  action: 'career_path_discovery',
};

interface CoachQuickStartsProps {
  onSelect: (analysisType: string) => void;
}

export const CoachQuickStarts = ({ onSelect }: CoachQuickStartsProps) => {
  const location = useLocation();
  const { profile } = useProfile();

  // Determine current page context
  let page = 'home';
  if (location.pathname === '/dashboard') {
    // Try to determine from location state
    const state = location.state as { activeTab?: string } | null;
    page = state?.activeTab || 'home';
  }

  const starters = [...(QUICK_STARTS[page] || QUICK_STARTS.home)];

  // Add career path discovery when no target role is set (on home and goals pages)
  if (!profile?.target_job_role && (page === 'home' || page === 'goals')) {
    starters.push(CAREER_PATH_QUICK_START);
  }

  return (
    <div className="px-4 pb-3">
      <p className="text-xs text-muted-foreground mb-2">Quick starts</p>
      <div className="flex flex-wrap gap-2">
        {starters.map((starter) => {
          const Icon = starter.icon;
          return (
            <Button
              key={starter.label}
              variant="outline"
              size="sm"
              className="gap-1.5 h-8 text-xs"
              onClick={() => onSelect(starter.action)}
            >
              <Icon className="w-3.5 h-3.5" />
              {starter.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};
